import {openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {Fragment} from 'react';

import {isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {catchUnhandledDataGridActions, DmsDataGrid, logError, Main, PageTab} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {DailyStockTakeDetailDialog} from './components/DailyStockTakeDetailDialog';
import {MonthlyStockTakeDetailDialog} from './components/MonthlyStockTakeDetailDialog';

const DAILY_GRID_CODE = 'report-daily-stock-take';
const MONTHLY_GRID_CODE = 'report-monthly-stock-take';
const DAILY_DETAIL_TEST_ID = 'dailyStockTakeDetail';
const MONTHLY_DETAIL_TEST_ID = 'monthlyStockTakeDetail';

export function InventoryClosingReport() {
  const dailyActionCallback: ActionCallback = (params) => {
    const openDetail = () => {
      // A bulk action hands over every selected row; the detail only makes sense for a single
      // one. Reported rather than silently dropped — it would mean the grid definition wired
      // this action up as a bulk one.
      if (!isString(params.rowId)) {
        return logError(
          `Bulk invocation of single-row action ${params.actionKey} is not implemented`
        );
      }

      const rowId = params.rowId;

      // Keeps the context-menu action disabled while its dialog is open. Optional on purpose:
      // the type promises an actionLock, but only the EagGrid context-menu path supplies a real
      // one — its plain row click passes a no-op, and the legacy grid engine passes nothing.
      const actionLock = params.actionLock;
      actionLock?.lock();

      openDialog(<DailyStockTakeDetailDialog id={rowId} data-testid={DAILY_DETAIL_TEST_ID} />, {
        /**
         * A per-row id, because the plain row click has no working lock to throttle it: without
         * one, `openDialog` generates a fresh key every time and a double-click stacks two
         * identical dialogs. With it, the second open replaces the first.
         */
        id: `${DAILY_DETAIL_TEST_ID}-${rowId}`,
        title: i18n.t('entity.warehouse.reporting.dailyStockTakeDetail'),
        size: 'large',
        scrollBehavior: 'outside',
        onCloseComplete: () => actionLock?.unlock(),
        'data-testid': DAILY_DETAIL_TEST_ID,
      });
    };

    /**
     * The grid definition declares `redirectDetail`; the grid itself adds an "open in new tab"
     * context-menu item that dispatches the `_newTab` variant, and a dialog has no URL to open
     * separately, so both show the same detail.
     */
    match(params.actionKey)
      .with('redirectDetail', 'redirectDetail_newTab', openDetail)
      .otherwise(() => catchUnhandledDataGridActions(params.actionKey));
  };

  const monthlyActionCallback: ActionCallback = (params) => {
    const openDetail = () => {
      if (!isString(params.rowId)) {
        return logError(
          `Bulk invocation of single-row action ${params.actionKey} is not implemented`
        );
      }

      const rowId = params.rowId;
      const actionLock = params.actionLock;
      actionLock?.lock();

      openDialog(<MonthlyStockTakeDetailDialog id={rowId} data-testid={MONTHLY_DETAIL_TEST_ID} />, {
        id: `${MONTHLY_DETAIL_TEST_ID}-${rowId}`,
        title: i18n.t('entity.warehouse.reporting.monthlyStockTakeDetail'),
        size: 'large',
        scrollBehavior: 'outside',
        onCloseComplete: () => actionLock?.unlock(),
        'data-testid': MONTHLY_DETAIL_TEST_ID,
      });
    };

    match(params.actionKey)
      .with('redirectDetail', 'redirectDetail_newTab', openDetail)
      .otherwise(() => catchUnhandledDataGridActions(params.actionKey));
  };

  const tabs: PageTab[] = [
    {
      content: (
        <Fragment key="warehouse-daily-inventory-closing">
          <DmsDataGrid
            gridCode={DAILY_GRID_CODE}
            actionCallback={dailyActionCallback}
            data-testid={testIds.warehouse.reportingInventoryClosing('datagrid')}
          />
        </Fragment>
      ),
      id: 'warehouse-daily-inventory-closing',
      title: i18n.t('page.warehouse.labels.dailyInventoryClosing'),
      href: warehouseRoutes.reportingInventoryClosing,
    },
    {
      content: (
        <Fragment key="warehouse-monthly-inventory-closing">
          <DmsDataGrid
            gridCode={MONTHLY_GRID_CODE}
            actionCallback={monthlyActionCallback}
            data-testid={testIds.warehouse.reportingInventoryClosing('monthlyDatagrid')}
          />
        </Fragment>
      ),
      id: 'warehouse-monthly-inventory-closing',
      title: i18n.t('page.warehouse.labels.monthlyInventoryClosing'),
      href: warehouseRoutes.reportingInventoryClosingMonthly,
    },
  ];

  return <Main isFullHeight tabs={tabs} />;
}
